import mapCustomerInformationFields from "../../../lib/mapCustomerInformationFields";
import {
  getTLDsFromCart,
  getOnecomConfig,
  getCustomerInformationFromLocalStorage,
  getAddonProductsInCart,
  isDomainPresent,
  hasVPSInCart,
} from "../../selectors";

import { loadUserDataFromSession } from "../loadUserDataFromSession/loadUserDataFromSession";
import { loadCountryRestrictions } from "../loadCountryRestrictions/loadCountryRestrictions";
import { setCountryCodeInSession } from "../setCountryCodeInSession/setCountryCodeInSession";
import { setCustomerInformationInLocalStorage } from "../setCustomerInformationInLocalStorage/setCustomerInformationInLocalStorage";
import { goToRoute } from "../goToRoute/goToRoute";

export const loadCustomerInformationFields = ({
  customerType = "PRIVATE",
  isRegistrant = true,
  country,
  includeOptions = true,
  firstTimeLoad,
  citizenship = null,
}) => {
  return async (dispatch, getState, { api }) => {
    const { countryCode } = getOnecomConfig(getState());
    let tlds = getTLDsFromCart(getState()).join(", ");
    const isDomain = isDomainPresent(getState());
    const addonProducts = getAddonProductsInCart(getState());
    const activeFormFromStore = getState().customerInformation.activeForm;
    const hasVPS = hasVPSInCart(getState());

    if (!isDomain && !hasVPS) {
      dispatch(goToRoute({ pageName: "domainSearch" }));
    }

    let userSavedValues = null;

    // Check if user data is stored in the the backend
    const payload = await dispatch(loadUserDataFromSession());

    const savedData =
      activeFormFromStore === "registrantForm"
        ? payload.data.registrant
        : payload.data.customer;

    if (firstTimeLoad && payload.wasSuccessful && savedData) {
      const { customer, registrant, isSame } = payload.data;

      userSavedValues =
        activeFormFromStore === "registrantForm" ? registrant : customer;

      if (firstTimeLoad && userSavedValues) {
        country = isSame ? registrant.country : customer.country;
        customerType = isSame ? registrant.type : customer.type || "PRIVATE";
        isRegistrant =
          activeFormFromStore === "customer" ? isSame : isRegistrant;
      }
    }
    // If user data is not stored in the backend
    // Check if user data is saved in the localStorage
    else if (!userSavedValues) {
      const retrievedCustomerInformationData =
        getCustomerInformationFromLocalStorage(getState());
      if (retrievedCustomerInformationData) {
        const { activeForm, customer, registrant, isSame } =
          retrievedCustomerInformationData;

        if (activeForm === "registrantForm") {
          userSavedValues = registrant;
          country = registrant && registrant.country;
          citizenship = citizenship || (registrant && registrant.citizenship);
          customerType = (registrant && registrant.type) || customerType;
        } else {
          userSavedValues = customer;
          isRegistrant = typeof isSame === "boolean" ? isSame : isRegistrant;
          country = customer && customer.country;
          citizenship = citizenship || (customer && customer.citizenship);

          customerType = (customer && customer.type) || customerType;
        }
        citizenship = !firstTimeLoad && citizenship ? citizenship : null;
      }
    }

    country = country || countryCode;

    if (activeFormFromStore === "customer") {
      // Default countryCode in the session based on country value from the Owner form
      await dispatch(setCountryCodeInSession(country));
    }

    const exchangeProducts = [
      "EXCHANGE_ONLINE",
      "EXCHANGE_M365_BUSINESS_BASIC",
      "EXCHANGE_M365_BUSINESS_STANDARD",
    ];

    const isExchangeAmongAddons = addonProducts.some(({ name }) =>
      exchangeProducts.includes(name),
    );

    if (isExchangeAmongAddons) {
      // Check if Exchange is restricterd
      await dispatch(loadCountryRestrictions(country));
    }

    return api
      .getCustomerInformationFields(
        customerType.toUpperCase(),
        country,
        tlds,
        isRegistrant,
        citizenship,
        includeOptions,
      )
      .then(
        async (data) => {
          if (data.result.relevantFields.length > 0) {
            const relevantFields = data.result.relevantFields;

            const typeField = relevantFields.find(
              (field) => field.infoFieldName === "type",
            );

            const validatedTypeSentByBackend =
              typeField.options.find((option) => option.selected) ||
              typeField.options[0];

            // If the previously saved type (in localStorage or the backend session)
            // does not match what the backend returns, overwrite it.

            // This is a scenario, how user can end up in the situation when customer type is out of sync:
            // 1. customer adds a domain with any TLD except .name to the cart
            // 2. in the Customer Info page, the customer changes the type to Company and provides the required information
            // 3. customer clicks Continue, submitting this data to the backend session
            // 4. customer goes back to the domain search page.
            // 5. customer removes the previously added domain and adds a new domain with the .name TLD.
            // 6. customer returns to the Customer Info page, but .name domains are not allowed for the Company type.
            // When the frontend calls the required-field endpoint using the previously saved type (Company), the result is the backend returns Company field with "private" as a customer type
            if (
              userSavedValues &&
              userSavedValues["type"] !== validatedTypeSentByBackend.value
            ) {
              // Clear backend session data if customer submitted an unsupported Type,
              // because the type may be incompatible with the added TLD.
              if (payload.data.customer?.type) {
                await api.clearCustomerData("CUSTOMER_REGISTRANT");
              }

              dispatch(
                setCustomerInformationInLocalStorage(
                  "type",
                  validatedTypeSentByBackend.value,
                ),
              );

              return dispatch(
                loadCustomerInformationFields({
                  customerType: validatedTypeSentByBackend.value,
                  isRegistrant,
                  country,
                  includeOptions,
                  firstTimeLoad: false,
                  citizenship,
                }),
              );
            }

            const fieldsToRender = mapCustomerInformationFields(
              relevantFields,
              userSavedValues,
            );

            return {
              wasSuccessful: true,
              fields: fieldsToRender,
              isSame: isRegistrant,
            };
          }

          return {
            wasSuccessful: false,
          };
        },
        (err) => {
          return { wasSuccessful: false };
        },
      );
  };
};
